export default function BenefitsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Benefits of Selling to <strong>Cash Home Buyers</strong>
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            When you have title issues, selling to traditional buyers is nearly impossible. Here's why <strong>cash home buyers in El Paso</strong> are your best option.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/ea72679dfb7f0417741f8b17bacf38ef.jpg"
              alt="Happy homeowner after selling house with title issues"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
          <div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">All-Cash Offers</h3>
                  <p className="text-gray-700 leading-relaxed">
                    No mortgage contingencies means no lender requirements. We can close even with title defects that would kill traditional sales.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Closing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Close in 7-14 days instead of waiting months or years to resolve title issues before selling.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">We Pay Off Liens</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All liens are paid at closing from the sale proceeds. You never have to come out of pocket.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-heart-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Buy As-Is</h3>
                  <p className="text-gray-700 leading-relaxed">
                    No repairs, no cleaning, no staging. We buy houses in any condition, with any title issues.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">We Handle Paperwork</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All the complicated legal work, negotiations, and documentation - we take care of everything.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-percent-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Fees or Commissions</h3>
                  <p className="text-gray-700 leading-relaxed">
                    No real estate agent commissions (6%), no closing costs, no hidden fees. Our offer is what you get.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Traditional Sale vs. Selling to Us
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-4 text-gray-900 font-bold">Factor</th>
                  <th className="text-center py-4 px-4 text-red-600 font-bold">Traditional Sale</th>
                  <th className="text-center py-4 px-4 text-emerald-600 font-bold">Selling to T-Rex</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 font-semibold text-gray-900">Timeline</td>
                  <td className="py-4 px-4 text-center text-gray-700">6-24 months (after resolving title issues)</td>
                  <td className="py-4 px-4 text-center text-gray-700">7-14 days</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-4 font-semibold text-gray-900">Lien Resolution</td>
                  <td className="py-4 px-4 text-center text-gray-700">You pay out of pocket before listing</td>
                  <td className="py-4 px-4 text-center text-gray-700">We pay at closing from proceeds</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 font-semibold text-gray-900">Title Issues</td>
                  <td className="py-4 px-4 text-center text-gray-700">Must be resolved before sale</td>
                  <td className="py-4 px-4 text-center text-gray-700">We buy with issues present</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-4 font-semibold text-gray-900">Repairs</td>
                  <td className="py-4 px-4 text-center text-gray-700">Required for market value</td>
                  <td className="py-4 px-4 text-center text-gray-700">None - we buy as-is</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 font-semibold text-gray-900">Commissions</td>
                  <td className="py-4 px-4 text-center text-gray-700">6% to agents ($12,000 on $200k)</td>
                  <td className="py-4 px-4 text-center text-gray-700">$0 - no commissions</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-4 font-semibold text-gray-900">Closing Costs</td>
                  <td className="py-4 px-4 text-center text-gray-700">2-3% ($4,000-$6,000 on $200k)</td>
                  <td className="py-4 px-4 text-center text-gray-700">$0 - we cover closing costs</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4 font-semibold text-gray-900">Carrying Costs</td>
                  <td className="py-4 px-4 text-center text-gray-700">$700/month for 12+ months = $8,400+</td>
                  <td className="py-4 px-4 text-center text-gray-700">Minimal - close in 2 weeks</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="py-4 px-4 font-semibold text-gray-900">Legal Fees</td>
                  <td className="py-4 px-4 text-center text-gray-700">$5,000-$15,000+ for title work</td>
                  <td className="py-4 px-4 text-center text-gray-700">$0 - we handle all legal work</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="py-4 px-4 font-bold text-gray-900">Total Cost/Time</td>
                  <td className="py-4 px-4 text-center font-bold text-red-600">$25,000-$40,000+ / 12-24 months</td>
                  <td className="py-4 px-4 text-center font-bold text-emerald-600">$0 / 7-14 days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}